import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import swal from 'sweetalert';
import SwitcherService from '../../../services/SwitcherService';
import { PageHeader, Card, Button, Badge, EmptyState } from '../../ui';

const SwitcherList = () => {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const ok = await SwitcherService.access();
      setAllowed(ok);
      if (ok) setItems(await SwitcherService.list());
    } catch { setAllowed(false); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const name = await swal({
      title: 'New switcher',
      text: 'Name this multi-camera switcher',
      content: { element: 'input', attributes: { placeholder: 'e.g. Main Event', value: '' } },
      buttons: ['Cancel', 'Create'],
    });
    if (!name) return;
    setCreating(true);
    try {
      const sw = await SwitcherService.create(String(name).trim() || 'Switcher');
      nav(`/switcher/${sw.id}`);
    } catch (e) {
      swal('Error', e.response?.data?.error || 'Failed to create switcher', 'error');
    } finally { setCreating(false); }
  };

  const del = (sw) => swal({
    title: 'Delete switcher?', text: `"${sw.name}" and its program output stream will be removed.`,
    icon: 'warning', buttons: ['Cancel', 'Delete'], dangerMode: true,
  }).then(async (ok) => { if (!ok) return; await SwitcherService.remove(sw.id); load(); });

  return (
    <>
      <PageHeader
        title="Live Switcher"
        subtitle="Combine several camera streams into one program output and cut between them live"
        actions={<Button icon="fa-plus" onClick={create} disabled={creating}>New switcher</Button>}
      />

      {loading ? (
        <div className="tw-py-16 tw-text-center"><i className="fa-solid fa-spinner fa-spin tw-text-2xl tw-text-brand" /></div>
      ) : allowed === false ? (
        <Card>
          <EmptyState
            icon="fa-lock"
            title="The live switcher isn't in your plan"
            hint="Upgrade to a plan that includes the multi-camera live switcher to combine feeds and cut a program output."
            action={<Button icon="fa-tags" onClick={() => nav('/pricing')}>View plans</Button>}
          />
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            icon="fa-sliders"
            title="No switchers yet"
            hint="Create a switcher, add your camera streams as inputs, then cut between them during a live event."
            action={<Button icon="fa-plus" onClick={create}>New switcher</Button>}
          />
        </Card>
      ) : (
        <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2 lg:tw-grid-cols-3">
          {items.map((sw) => (
            <Card key={sw.id} className="tw-cursor-pointer hover:tw-shadow-pop" bodyClass="tw-p-4">
              <div onClick={() => nav(`/switcher/${sw.id}`)}>
                <div className="tw-flex tw-items-start tw-justify-between">
                  <div className="tw-font-bold tw-text-heading">{sw.name}</div>
                  <Badge tone={sw.status === 'live' ? 'live' : 'neutral'}>
                    {sw.status === 'live' ? 'ON AIR' : 'Idle'}
                  </Badge>
                </div>
                <div className="tw-mt-2 tw-text-xs tw-text-content-muted">
                  {sw.input_count || 0} input{sw.input_count === 1 ? '' : 's'} · program: {sw.program_name || '—'}
                </div>
              </div>
              <div className="tw-mt-3 tw-flex tw-gap-2">
                <Button size="sm" variant="secondary" icon="fa-arrow-right" onClick={() => nav(`/switcher/${sw.id}`)}>Open</Button>
                <Button size="sm" variant="ghost" icon="fa-trash" onClick={() => del(sw)} />
              </div>
            </Card>
          ))}
        </div>
      )}
    </>
  );
};

export default SwitcherList;
